import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { formatDate, copyToClipboard, sendEmail } from "../../lib/appUtils";
import { GlobalNav } from "../shared/GlobalNav";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { 
  FileText, Plus, Eye, Edit3, Send, Trash2, CheckCircle, 
  ArrowRight, AlertTriangle, Shield, Crown, Copy, Banknote,
  Clock, XCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SimpleQuoteCard = ({ quote }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 font-heading">{quote.title}</h3>
          <p className="text-gray-600 font-body">{quote.client_name} • £{quote.total_amount}</p>
          <Badge className="mt-2">
            {quote.status || 'Draft'}
          </Badge>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm">
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" size="sm">
            <Edit3 className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// Dedicated Quotes Page Component
const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateQuoteForm, setShowCreateQuoteForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  const { token, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch subscription features
  const fetchSubscriptionFeatures = async () => {
    try {
      const response = await axios.get(`${API}/subscription/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription features:', error);
    }
  };

  // Handle subscription upgrade
  const handleUpgradeSubscription = async (planType) => {
    try {
      const response = await axios.post(`${API}/subscription/create-checkout-session`, {
        plan_id: planType,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to start upgrade process. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Fetch user settings
  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  useEffect(() => {
    fetchQuotes();
    fetchSubscriptionFeatures();
    fetchUserSettings();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch quotes",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    if (filter === 'draft') return quote.status === 'draft';
    if (filter === 'sent') return quote.status === 'sent';
    if (filter === 'accepted') return quote.status === 'accepted';
    if (filter === 'converted') return quote.status === 'converted';
    return true; // 'all'
  });

  const getQuoteStats = () => {
    return {
      total: quotes.length,
      draft: quotes.filter(q => q.status === 'draft').length,
      sent: quotes.filter(q => q.status === 'sent').length,
      accepted: quotes.filter(q => q.status === 'accepted').length,
      totalValue: quotes.reduce((sum, q) => sum + (q.total_amount || 0), 0)
    };
  };

  const stats = getQuoteStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <GlobalNav user={user} onLogout={logout} currentPage="quotes" />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
            <p className="mt-4 text-gray-600 font-body">Loading quotes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNav user={user} onLogout={logout} currentPage="quotes" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">Quotes</h1>
              <p className="text-gray-600 font-body">Create professional quotes with deliverables and send to clients for approval.</p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Subscription Plan Indicator */}
              {subscriptionFeatures && (
                <div className="bg-blue-50 px-3 py-1 rounded-full">
                  <span className="text-xs font-medium text-blue-700 font-body">
                    {subscriptionFeatures.plan_info.name} Plan
                  </span>
                </div>
              )}
              
              {/* Create Quote Button with Feature Gating */}
              <Button 
                onClick={() => {
                  if (subscriptionFeatures?.features?.quotes) {
                    setShowCreateQuoteForm(true);
                  } else {
                    setShowUpgradeModal(true);
                  }
                }}
                className={subscriptionFeatures?.features?.quotes ? 
                  "bg-yellow-600 hover:bg-yellow-700 text-white font-body" : 
                  "bg-yellow-600 hover:bg-yellow-700 text-white font-body"
                }
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Quote
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <FileText className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.total}</p>
              <p className="text-sm text-gray-600 font-body">Total Quotes</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <Edit3 className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.draft}</p>
              <p className="text-sm text-gray-600 font-body">Draft</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <Send className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.sent}</p>
              <p className="text-sm text-gray-600 font-body">Sent</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">{stats.accepted}</p>
              <p className="text-sm text-gray-600 font-body">Accepted</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6 text-center">
              <Banknote className="w-8 h-8 text-[#e6007e] mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900 font-heading">£{(stats.totalValue || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-600 font-body">Total Value</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-4 mb-6">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border font-body ${
                filter === 'all' 
                  ? 'bg-yellow-600 text-white border-yellow-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All Quotes
            </button>
            <button
              onClick={() => setFilter('draft')}
              className={`px-4 py-2 text-sm font-medium border-t border-b font-body ${
                filter === 'draft' 
                  ? 'bg-yellow-600 text-white border-yellow-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Draft
            </button>
            <button
              onClick={() => setFilter('sent')}
              className={`px-4 py-2 text-sm font-medium border-t border-b font-body ${
                filter === 'sent' 
                  ? 'bg-yellow-600 text-white border-yellow-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Sent
            </button>
            <button
              onClick={() => setFilter('accepted')}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border font-body ${
                filter === 'accepted' 
                  ? 'bg-yellow-600 text-white border-yellow-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Accepted
            </button>
          </div>
        </div>

        {/* Quotes List */}
        <Card className="border-0 shadow-sm">
          <CardContent>
            {filteredQuotes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">
                  {filter === 'all' ? 'No quotes yet' : `No ${filter} quotes`}
                </h3>
                <p className="text-gray-600 font-body mb-6">
                  Create professional quotes with deliverables to send to your clients.
                </p>
                <Button 
                  onClick={() => setShowCreateQuoteForm(true)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-body"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Quote
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredQuotes.map((quote) => (
                  <QuoteCard 
                    key={quote.id} 
                    quote={quote} 
                    onUpdate={fetchQuotes}
                    user={user}
                    userSettings={userSettings}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Quote Modal */}
      {showCreateQuoteForm && (
        <CreateQuoteModal
          isOpen={showCreateQuoteForm}
          onClose={() => setShowCreateQuoteForm(false)}
          onSuccess={() => {
            setShowCreateQuoteForm(false);
            fetchQuotes();
          }}
        />
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Upgrade Required</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Quotes are available with <strong>Paify Plus</strong> subscription or higher.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Crown className="w-4 h-4 text-yellow-600" />
                  <span className="font-medium text-yellow-800 font-body">Paify Plus Features</span>
                </div>
                <ul className="mt-2 text-sm text-yellow-700 space-y-1 font-body">
                  <li>• Create professional quotes</li>
                  <li>• Send invoices to clients</li>
                  <li>• Add your branding & logo</li>
                  <li>• VAT receipts & reporting</li>
                  <li>• Trust badge verification</li>
                </ul>
              </div>
              <div className="flex justify-end space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowUpgradeModal(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    setShowUpgradeModal(false);
                    handleUpgradeSubscription('paidd_plus');
                  }}
                  className="bg-[#e6007e] hover:bg-pink-700 text-white font-body"
                >
                  Upgrade to Paify Plus
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


// Subscription components extracted to ./components/subscriptions/

// Dedicated Invoices Page

const QuoteClientViewContent = ({ quote, user, userSettings, previewMode, accepting, handleAcceptQuote }) => {
  return (
    <>
      {/* Quote Header with Logo */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-8">
          {userSettings?.logo_url && (
            <div className="flex justify-start mb-6">
              <img 
                src={userSettings.logo_url} 
                alt={`${user.business_name} Logo`}
                className="h-12 w-auto"
                style={{maxHeight: '80px', maxWidth: '200px', marginBottom: '20px', display: 'block'}}
              />
            </div>
          )}
          
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">{quote.title}</h1>
              <p className="text-gray-600 font-body">Quote for {quote.client_name}</p>
              {user?.business_name && (
                <p className="text-sm text-gray-500 font-body">From {user.business_name}</p>
              )}
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-[#e6007e] font-heading">
                £{(quote.total_amount || 0).toLocaleString()}
              </div>
              <p className="text-sm text-gray-600 font-body">
                Valid until {new Date(quote.expires_at).toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Badge className={
              quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
              quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            }>
              {quote.status === 'accepted' ? 'Accepted' :
               quote.status === 'sent' ? 'Awaiting Your Response' :
               'Draft'}
            </Badge>
            
            {quote.delivery_timeline && (
              <span className="text-sm text-gray-600 font-body">
                Timeline: {quote.delivery_timeline}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Work Description */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading">Project Description</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700 font-body whitespace-pre-line">{quote.work_description}</p>
          </div>
        </CardContent>
      </Card>

      {/* Deliverables Section */}
      {quote.include_deliverables && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-heading">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Project Deliverables</span>
            </CardTitle>
            <p className="text-sm text-gray-600 font-body">What's included in this project</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-green-50 rounded-lg p-4">
                <div className="prose prose-sm max-w-none">
                  <div className="text-gray-700 font-body whitespace-pre-line">{quote.deliverables_text}</div>
                </div>
              </div>

              {quote.revision_allowance && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2 font-heading">Revision Policy</h4>
                  <p className="text-blue-700 font-body">{quote.revision_allowance}</p>
                </div>
              )}

              {quote.completion_guarantee && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2 font-heading">Completion Guarantee</h4>
                  <p className="text-purple-700 font-body">
                    Work will be completed to the agreed specifications regardless of payment timing, 
                    in accordance with Consumer Rights Act 2015 and standard business practices.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quote Actions for Client */}
      {quote.status === 'sent' && !previewMode && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading">Ready to proceed?</h3>
            <p className="text-gray-600 font-body mb-6">
              By accepting this quote, you agree to the project description, deliverables, and terms outlined above.
              Your service provider will then send you the contract and payment options.
            </p>
            
            <div className="flex justify-center space-x-4">
              <Button
                onClick={handleAcceptQuote}
                disabled={accepting}
                className="bg-green-600 hover:bg-green-700 text-white font-body px-8 py-3 text-lg"
              >
                {accepting ? "Accepting..." : "Accept Quote"}
              </Button>
              
              <div className="text-xs text-gray-500 font-body self-center max-w-xs">
                Need changes? Contact your service provider directly to discuss modifications.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {previewMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-blue-700 font-body">
            <strong>Preview Mode:</strong> This is exactly how your client will see the quote when you send it to them.
          </p>
        </div>
      )}
    </>
  );
};

// Quote Client View Component - Main wrapper
const QuoteClientView = ({ quote, user, userSettings, previewMode = false }) => {
  const [accepting, setAccepting] = useState(false);
  const { toast } = useToast();

  const handleAcceptQuote = async () => {
    if (previewMode) {
      toast({
        title: "Preview Mode",
        description: "This is preview mode - actual acceptance only works for clients",
        variant: "destructive"
      });
      return;
    }

    setAccepting(true);
    try {
      const response = await axios.post(`${API}/quote/${quote.slug}/accept`, {
        client_ip: "client_ip_here",
        user_agent: navigator.userAgent
      });

      // Immediately redirect to checkout flow (no stopping at "accepted")
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to accept quote",
        variant: "destructive"
      });
      setAccepting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {!previewMode && (
        <header className="bg-white border-b border-gray-200 mb-8">
          <div className="px-4 py-4">
            <div className="flex items-center space-x-4">
              {userSettings?.logo_url && (
                <img 
                  src={userSettings.logo_url} 
                  alt={`${user?.business_name} Logo`}
                  className="h-8 w-auto"
                />
              )}
              <div className="text-2xl font-bold brand-logo font-heading">
                {user?.business_name || 'Business'}
              </div>
              <span className="text-gray-400">•</span>
              <span className="text-lg font-semibold text-gray-700 font-heading">Project Quote</span>
            </div>
          </div>
        </header>
      )}

      {/* Trust Badge - Top Right Corner */}
      {userSettings?.trust_badge_enabled && (
        <div className="flex justify-end mb-4">
          <div className="bg-gradient-to-r from-[#e6007e] to-pink-600 text-white px-3 py-1 rounded-full flex items-center space-x-2 text-xs font-medium shadow-lg">
            <Shield className="w-3 h-3" />
            <span>Secured by Paify</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Quote Header with Logo */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-8">
            {userSettings?.logo_url && (
              <div className="flex justify-start mb-6">
                <img 
                  src={userSettings.logo_url} 
                  alt={`${user.business_name} Logo`}
                  className="h-12 w-auto"
                  style={{maxHeight: '80px', maxWidth: '200px', marginBottom: '20px', display: 'block'}}
                />
              </div>
            )}
            
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 font-heading">{quote.title}</h1>
                <p className="text-gray-600 font-body">Quote for {quote.client_name}</p>
                {user?.business_name && (
                  <p className="text-sm text-gray-500 font-body">From {user.business_name}</p>
                )}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-[#e6007e] font-heading">
                  £{(quote.total_amount || 0).toLocaleString()}
                </div>
                <p className="text-sm text-gray-600 font-body">
                  Valid until {new Date(quote.expires_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Badge className={
                quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                quote.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }>
                {quote.status === 'accepted' ? 'Accepted' :
                 quote.status === 'sent' ? 'Awaiting Your Response' :
                 'Draft'}
              </Badge>
              
              {quote.delivery_timeline && (
                <span className="text-sm text-gray-600 font-body">
                  Timeline: {quote.delivery_timeline}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Work Description */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading">Project Description</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-gray-700 font-body whitespace-pre-line">{quote.work_description}</p>
            </div>
          </CardContent>
        </Card>

        {/* Deliverables Section */}
        {quote.include_deliverables && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 font-heading">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Project Deliverables</span>
              </CardTitle>
              <p className="text-sm text-gray-600 font-body">What's included in this project</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="prose prose-sm max-w-none">
                    <div className="text-gray-700 font-body whitespace-pre-line">{quote.deliverables_text}</div>
                  </div>
                </div>

                {quote.revision_allowance && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 font-heading">Revision Policy</h4>
                    <p className="text-blue-700 font-body">{quote.revision_allowance}</p>
                  </div>
                )}

                {quote.completion_guarantee && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-2 font-heading">Completion Guarantee</h4>
                    <p className="text-purple-700 font-body">
                      Work will be completed to the agreed specifications regardless of payment timing, 
                      in accordance with Consumer Rights Act 2015 and standard business practices.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quote Actions for Client */}
        {quote.status === 'sent' && !previewMode && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading">Ready to proceed?</h3>
              <p className="text-gray-600 font-body mb-6">
                By accepting this quote, you agree to the project description, deliverables, and terms outlined above.
                Your service provider will then send you the contract and payment options.
              </p>
              
              <div className="flex justify-center space-x-4">
                <Button
                  onClick={handleAcceptQuote}
                  disabled={accepting}
                  className="bg-green-600 hover:bg-green-700 text-white font-body px-8 py-3 text-lg"
                >
                  {accepting ? "Accepting..." : "Accept Quote"}
                </Button>
                
                <div className="text-xs text-gray-500 font-body self-center max-w-xs">
                  Need changes? Contact your service provider directly to discuss modifications.
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {previewMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-blue-700 font-body">
              <strong>Preview Mode:</strong> This is exactly how your client will see the quote when you send it to them.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Quote Modal Component  

const EditQuoteModal = ({ quote, isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: quote?.title || '',
    client_name: quote?.client_name || '',
    client_email: quote?.client_email || '',
    client_company: quote?.client_company || '',
    work_description: quote?.work_description || '',
    total_amount: quote?.total_amount || '',
    include_deliverables: quote?.include_deliverables || true,
    deliverables_text: quote?.deliverables_text || '',
    delivery_timeline: quote?.delivery_timeline || '',
    revision_allowance: quote?.revision_allowance || '',
    completion_guarantee: quote?.completion_guarantee || true,
    valid_for_days: quote?.valid_for_days || 30
  });
  
  const [saving, setSaving] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/quotes/${quote.id}`, {
        title: formData.title,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_company: formData.client_company,
        work_description: formData.work_description,
        total_amount: parseFloat(formData.total_amount),
        include_deliverables: formData.include_deliverables,
        deliverables_text: formData.deliverables_text,
        delivery_timeline: formData.delivery_timeline,
        revision_allowance: formData.revision_allowance,
        completion_guarantee: formData.completion_guarantee,
        valid_for_days: parseInt(formData.valid_for_days)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Quote Updated",
        description: "Quote has been updated successfully"
      });

      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update quote",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Quote</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Project Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Website Redesign Project"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Total Amount</Label>
                  <Input
                    type="number"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                    placeholder="5000"
                    step="0.01"
                    required
                    className="font-body"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Client Name</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="John Doe"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Client Email</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="john@company.com"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Company (Optional)</Label>
                  <Input
                    value={formData.client_company}
                    onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                    placeholder="Company Ltd"
                    className="font-body"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Work Description & Pricing */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Project Description & Pricing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Work Description</Label>
                  <Textarea
                    value={formData.work_description}
                    onChange={(e) => setFormData({...formData, work_description: e.target.value})}
                    placeholder="Complete website redesign including new branding, responsive design, and SEO optimization..."
                    required
                    className="font-body"
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Delivery Timeline</Label>
                    <Input
                      value={formData.delivery_timeline}
                      onChange={(e) => setFormData({...formData, delivery_timeline: e.target.value})}
                      placeholder="4-6 weeks from project start"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Quote Valid For (Days)</Label>
                    <Select value={formData.valid_for_days.toString()} onValueChange={(value) => setFormData({...formData, valid_for_days: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deliverables Section */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center justify-between font-heading">
                <span>Project Deliverables</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-body">Include deliverables</span>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, include_deliverables: !formData.include_deliverables})}
                    className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                      formData.include_deliverables ? 'bg-[#e6007e]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        formData.include_deliverables ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            
            {formData.include_deliverables && (
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">What's Included</Label>
                    <Textarea
                      value={formData.deliverables_text}
                      onChange={(e) => setFormData({...formData, deliverables_text: e.target.value})}
                      placeholder="• Initial design concepts (3 options)
• Up to 2 rounds of revisions
• Responsive design for mobile and desktop
• Basic SEO setup
• 30 days of post-launch support

What's NOT included:
• Content writing (additional £500)
• E-commerce functionality
• Additional pages beyond agreed scope"
                      className="font-body"
                      rows={8}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Revision Allowance</Label>
                    <Input
                      value={formData.revision_allowance}
                      onChange={(e) => setFormData({...formData, revision_allowance: e.target.value})}
                      placeholder="2 rounds of revisions included, additional revisions £250 each"
                      className="font-body"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      checked={formData.completion_guarantee}
                      onCheckedChange={(checked) => setFormData({...formData, completion_guarantee: checked})}
                    />
                    <span className="text-sm font-body">
                      Include completion guarantee (work completed even after upfront payment)
                    </span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="font-body">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-black hover:bg-gray-800 text-white font-body">
              {saving ? "Updating..." : "Update Quote"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Quote Card Component - Enhanced with Preview, Edit, PDF

const QuoteCard = ({ quote, onUpdate, user, userSettings = null }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { toast } = useToast();
  const { token } = useAuth();
  const navigate = useNavigate();

  const getQuoteStatus = (quote) => {
    switch (quote.status) {
      case 'draft': return { label: 'Draft', color: 'bg-gray-100 text-gray-700' };
      case 'sent': return { label: 'Sent to Client', color: 'bg-blue-100 text-blue-700' };
      case 'accepted': return { label: 'Accepted', color: 'bg-green-100 text-green-700' };
      case 'declined': return { label: 'Declined', color: 'bg-red-100 text-red-700' };
      case 'converted': return { label: 'Converted to Project', color: 'bg-purple-100 text-purple-700' };
      default: return { label: 'Unknown', color: 'bg-gray-100 text-gray-700' };
    }
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleConvertToProject = async () => {
    try {
      const response = await axios.post(`${API}/quotes/${quote.id}/convert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Quote Converted",
        description: "Quote has been converted to active project successfully"
      });
      
      onUpdate();
      navigate(`/projects/${response.data.project_id}`);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to convert quote to project",
        variant: "destructive"
      });
    }
  };

  const handleSendQuote = async () => {
    try {
      // Use the same email pattern that works for invoices
      const emailData = {
        to: quote.client_email,
        subject: `Quote for ${quote.title} - ${user?.business_name}`,
        template: 'payment_link',
        project_id: quote.id,
        payment_url: quote.quote_url,
        email_type: 'quote_link'
      };
      
      // Use the existing working sendEmail function
      const emailSent = await sendEmail(emailData, toast);
      
      if (emailSent) {
        // Update quote status to sent
        await axios.post(`${API}/quotes/${quote.id}/send`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast({
          title: "Quote Sent",
          description: `Quote has been sent to ${quote.client_email}`
        });
        
        onUpdate();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send quote",
        variant: "destructive"
      });
    }
  };

  const statusInfo = getQuoteStatus(quote);

  return (
    <>
      <Card className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-4 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 font-heading">{quote.title}</h3>
                <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              </div>
              
              <div className="flex items-center space-x-6 text-sm text-gray-600 mb-4">
                <span className="font-body">{quote.client_name}</span>
                <span className="font-body">•</span>
                <span className="font-body">{quote.client_email}</span>
                <span className="font-body">•</span>
                <span className="font-body">Created {formatDate(quote.created_at)}</span>
              </div>

              <div className="flex items-center space-x-4 text-sm">
                <span className="text-2xl font-bold text-gray-900 font-heading">
                  £{(quote.total_amount || 0).toLocaleString()}
                </span>
                <span className="text-gray-600 font-body">
                  {quote.include_deliverables ? 'With deliverables' : 'Quote only'}
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Preview Quote - Most Important */}
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
                className="font-body bg-blue-50 text-blue-700 border-blue-200"
                title="Preview what client will see"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>

              {/* Edit Quote */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleEdit}
                className="font-body"
                title="Edit quote details"
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Edit
              </Button>

              {/* Delete Quote */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600 hover:text-red-900 font-body"
                title="Delete quote"
              >
                <Trash2 className="w-4 h-4" />
              </Button>

              {quote.status === 'draft' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendQuote}
                  className="font-body bg-green-50 text-green-700 border-green-200"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Quote
                </Button>
              )}
              
              {/* Convert to Project - Always Available */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleConvertToProject}
                className="font-body bg-purple-50 text-purple-700 border-purple-200"
                title="Convert this quote to a full project"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Convert to Project
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quote Preview Modal - Shows Exact Client View */}
      {showPreview && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Quote Preview - Client View</DialogTitle>
              <p className="text-sm text-gray-600 font-body">This is exactly what your client will see</p>
            </DialogHeader>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <QuoteClientView quote={quote} user={user} userSettings={userSettings} previewMode={true} />
            </div>
            
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 font-body">
                <p>Quote URL: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{quote.quote_url}</code></p>
              </div>
              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  onClick={() => window.print()} 
                  className="font-body"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Print/Save as PDF
                </Button>
                <Button variant="outline" onClick={() => setShowPreview(false)} className="font-body">
                  Close Preview
                </Button>
                <Button
                  onClick={(e) => {
                    setShowPreview(false);
                    handleSendQuote();
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white font-body"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send to Client
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Quote Edit Modal */}
      {showEditForm && (
        <EditQuoteModal
          quote={quote}
          isOpen={showEditForm}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            setShowEditForm(false);
            onUpdate();
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Delete Quote</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-gray-700 font-body">
                Are you sure you want to delete <strong>{quote.title}</strong>? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800 font-body">
                    All quote data will be permanently lost.
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDialog(false)}
                  className="font-body"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await axios.delete(`${API}/quotes/${quote.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast({ title: "Quote Deleted", description: "Quote has been deleted successfully" });
                      setShowDeleteDialog(false);
                      onUpdate();
                    } catch (error) {
                      toast({ title: "Error", description: "Failed to delete quote", variant: "destructive" });
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white font-body"
                >
                  Delete Permanently
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// Create Quote Modal Component

const CreateQuoteModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    client_email: '',
    client_company: '',
    work_description: '',
    total_amount: '',
    include_deliverables: true,
    deliverables_text: '',
    delivery_timeline: '',
    revision_allowance: '',
    completion_guarantee: true,
    valid_for_days: 30
  });
  
  const [creating, setCreating] = useState(false);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const { token } = useAuth();
  const { toast } = useToast();

  // Fetch client suggestions from existing projects and quotes
  const fetchClientSuggestions = async () => {
    try {
      const [projectsResponse, quotesResponse] = await Promise.all([
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` }})
      ]);

      const clients = new Map();
      
      // Add clients from projects
      projectsResponse.data.forEach(project => {
        if (project.client_email) {
          clients.set(project.client_email, {
            name: project.client_name,
            email: project.client_email,
            company: project.client_company || ''
          });
        }
      });

      // Add clients from quotes
      quotesResponse.data.forEach(quote => {
        if (quote.client_email) {
          clients.set(quote.client_email, {
            name: quote.client_name,
            email: quote.client_email,
            company: quote.client_company || ''
          });
        }
      });

      setClientSuggestions(Array.from(clients.values()));
    } catch (error) {
      console.error('Failed to fetch client suggestions:', error);
    }
  };

  // Fill only client name from suggestion
  const selectClientName = (client) => {
    setFormData(prev => ({
      ...prev,
      client_name: client.name
    }));
    setShowNameSuggestions(false);
  };

  // Fill only email from suggestion  
  const selectClientEmail = (client) => {
    setFormData(prev => ({
      ...prev,
      client_email: client.email
    }));
    setShowEmailSuggestions(false);
  };

  // Fill all client data (with confirmation)
  const selectFullClient = (client) => {
    setFormData(prev => ({
      ...prev,
      client_name: client.name,
      client_email: client.email,
      client_company: client.company
    }));
    setShowNameSuggestions(false);
    setShowEmailSuggestions(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchClientSuggestions();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const response = await axios.post(`${API}/quotes`, {
        title: formData.title,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_company: formData.client_company,
        work_description: formData.work_description,
        total_amount: parseFloat(formData.total_amount),
        include_deliverables: formData.include_deliverables,
        deliverables_text: formData.deliverables_text,
        delivery_timeline: formData.delivery_timeline,
        revision_allowance: formData.revision_allowance,
        completion_guarantee: formData.completion_guarantee,
        valid_for_days: parseInt(formData.valid_for_days)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: "Quote Created",
        description: "Quote has been created successfully. You can now send it to your client."
      });

      onSuccess();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create quote",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Create Quote</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Project Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Website Redesign Project"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Total Amount</Label>
                  <Input
                    type="number"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
                    placeholder="5000"
                    step="0.01"
                    required
                    className="font-body"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="relative">
                  <Label className="text-sm font-medium text-gray-700 font-body">Client Name</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => {
                      setFormData({...formData, client_name: e.target.value});
                      // Suggestions disabled to prevent email override issues
                    }}
                    placeholder="John Doe"
                    required
                    className="font-body"
                  />
                  
                  {/* Client Suggestions Dropdown - DISABLED to prevent email override issues */}
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Client Email</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="john@company.com"
                    required
                    className="font-body"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Company (Optional)</Label>
                  <Input
                    value={formData.client_company}
                    onChange={(e) => setFormData({...formData, client_company: e.target.value})}
                    placeholder="Company Ltd"
                    className="font-body"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Work Description & Pricing */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Project Description & Pricing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 font-body">Work Description</Label>
                  <Textarea
                    value={formData.work_description}
                    onChange={(e) => setFormData({...formData, work_description: e.target.value})}
                    placeholder="Complete website redesign including new branding, responsive design, and SEO optimization..."
                    required
                    className="font-body"
                    rows={4}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Delivery Timeline</Label>
                    <Input
                      value={formData.delivery_timeline}
                      onChange={(e) => setFormData({...formData, delivery_timeline: e.target.value})}
                      placeholder="4-6 weeks from project start"
                      className="font-body"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Quote Valid For (Days)</Label>
                    <Select value={formData.valid_for_days.toString()} onValueChange={(value) => setFormData({...formData, valid_for_days: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Deliverables Section */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center justify-between font-heading">
                <span>Project Deliverables</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-body">Include deliverables</span>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, include_deliverables: !formData.include_deliverables})}
                    className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
                      formData.include_deliverables ? 'bg-[#e6007e]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        formData.include_deliverables ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </CardTitle>
            </CardHeader>
            
            {formData.include_deliverables && (
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">What's Included</Label>
                    <Textarea
                      value={formData.deliverables_text}
                      onChange={(e) => setFormData({...formData, deliverables_text: e.target.value})}
                      placeholder="• Initial design concepts (3 options)
• Up to 2 rounds of revisions
• Responsive design for mobile and desktop
• Basic SEO setup
• 30 days of post-launch support

What's NOT included:
• Content writing (additional £500)
• E-commerce functionality
• Additional pages beyond agreed scope"
                      className="font-body"
                      rows={8}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700 font-body">Revision Allowance</Label>
                    <Input
                      value={formData.revision_allowance}
                      onChange={(e) => setFormData({...formData, revision_allowance: e.target.value})}
                      placeholder="2 rounds of revisions included, additional revisions £250 each"
                      className="font-body"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      checked={formData.completion_guarantee}
                      onCheckedChange={(checked) => setFormData({...formData, completion_guarantee: checked})}
                    />
                    <span className="text-sm font-body">
                      Include completion guarantee (work completed even after upfront payment)
                    </span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose} className="font-body">
              Cancel
            </Button>
            <Button type="submit" disabled={creating} className="bg-black hover:bg-gray-800 text-white font-body">
              {creating ? "Creating..." : "Create Quote"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Quote Viewing Page - Uses reusable QuoteClientView component

const QuoteViewPage = () => {
  const { slug } = useParams();
  const [quote, setQuote] = useState(null);
  const [user, setUser] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchQuote();
  }, [slug]);

  const fetchQuote = async () => {
    try {
      console.log('=== QUOTE FETCH DEBUG START ===');
      console.log('Fetching quote with slug:', slug);
      console.log('BACKEND_URL:', BACKEND_URL);
      console.log('API constant:', API);
      console.log('Full API URL:', `${API}/quote/${slug}`);
      
      // Use fetch instead of axios to avoid any interceptors
      const response = await fetch(`${API}/quote/${slug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      console.log('Response headers:', [...response.headers.entries()]);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Quote API response data:', data);
      console.log('Data type:', typeof data);
      console.log('Data keys:', Object.keys(data || {}));
      
      if (data && data.quote) {
        console.log('Quote object:', data.quote);
        console.log('Quote title:', data.quote.title);
        setQuote(data.quote);
        setUser(data.user);
        setUserSettings(data.user_settings);
        setExpired(data.expired || false);
        console.log('✅ Quote loaded successfully:', data.quote.title);
      } else {
        console.error('❌ Invalid quote data structure:', data);
        throw new Error('Invalid quote data structure - no quote field in response');
      }
      console.log('=== QUOTE FETCH DEBUG END ===');
    } catch (error) {
      console.error('=== QUOTE FETCH ERROR ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      toast({
        title: "Error",
        description: `Quote fetch failed: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Quote Not Found</h2>
            <p className="text-gray-600 font-body">This quote link may have expired or been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-8 text-center">
            <Clock className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Quote Expired</h2>
            <p className="text-gray-600 font-body">This quote has expired. Please contact us for an updated quote.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <QuoteClientView 
        quote={quote} 
        user={user}
        userSettings={userSettings}
        previewMode={false} 
      />
    </div>
  );
};

// Projects Page Component

export { SimpleQuoteCard, QuotesPage, QuoteClientViewContent, QuoteClientView, EditQuoteModal, QuoteCard, CreateQuoteModal, QuoteViewPage };
